<template>
  <v-card class="pa-4" outlined style="padding: 16px !important;">
    <div class="d-flex align-center justify-space-between mb-4">
      <h2>{{ catalog ? 'Edit Catalog' : 'Add Catalog' }}</h2>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-form ref="formRef">
      <!-- Catalog Name Input -->
      <div class="field">
        <label style="font-size: 16px; margin-bottom: 5px;">Catalog Name <span class="error--text">*</span></label>
        <v-text-field
          v-model="form.fields.catalog_name"
          :rules="rules.required"
          dense
          outlined
          placeholder="Enter catalog name"
          style="margin-bottom: -18px;"
        />
      </div>

      <!-- Business Manager Dropdown -->
      <div class="field mt-4" v-if="catalog">
        <label style="font-size: 16px; margin-bottom: 5px;">Business Manager</label>
        <v-select
          v-model="form.fields.fb_bm_id"
          :items="formattedBmList"
          item-text="displayName"
          item-value="id"
          dense
          outlined
          placeholder="Select Business Manager"
          :loading="isLoadingBms"
          style="margin-bottom: -18px;"
        />
      </div>

      <v-divider class="my-4"></v-divider>

      <!-- Two Column Layout -->
      <div class="select-products-container">
        <!-- Left Column: Available Products -->
        <div class="left-column">
          <div class="column-header d-flex align-center justify-space-between mb-3">
            <h3 class="font-weight-medium">Select Products</h3>
            <v-btn
              v-if="pagination.total > 0"
              text
              x-small
              color="primary"
              @click="selectAllAvailable"
              :loading="isLoading"
            >
              <v-icon left x-small>mdi-checkbox-multiple-marked</v-icon>
              Select All ({{ pagination.total }})
            </v-btn>
          </div>

          <v-text-field
            v-model="searchText"
            dense
            outlined
            placeholder="Enter keyword to search"
            prepend-inner-icon="mdi-magnify"
            @input="debouncedSearch"
            hide-details
            class="mb-3"
          />

          <div class="catalog-list">
            <div
              v-for="product in products"
              :key="product._id"
              class="catalog-item"
              :class="{ selected: isSelected(product._id) }"
              @click="toggleProduct(product)"
            >
              <div class="d-flex align-center">
                <div class="catalog-image-thumb mr-3">
                  <img :src="parseProductImage(product)" :alt="product.title" />
                </div>
                <div class="flex-grow-1">
                  <div class="catalog-name">{{ product.title }}</div>
                </div>
                <v-btn icon small :color="isSelected(product._id) ? 'error' : 'primary'">
                  <v-icon small>{{ isSelected(product._id) ? 'mdi-minus-circle' : 'mdi-plus-circle' }}</v-icon>
                </v-btn>
              </div>
            </div>

            <div v-if="products.length === 0 && !isLoading" class="empty-state">
              <v-icon large color="grey">mdi-package-variant</v-icon>
              <p class="grey--text mt-2">No products found</p>
            </div>

            <div v-if="isLoading" class="text-center pa-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
          </div>

          <div v-if="hasMoreProducts" class="text-center mt-3">
            <v-btn outlined small @click="loadMoreProducts" :loading="loadingMore">
              Load more
            </v-btn>
          </div>
        </div>

        <!-- Right Column: Selected Products -->
        <div class="right-column">
          <div class="column-header d-flex align-center justify-space-between mb-3">
            <h3 class="font-weight-medium">Selected Products ({{ selectedProducts.length }})</h3>
            <v-btn icon small @click="clearAllSelected" :disabled="selectedProducts.length === 0">
              <v-icon small>mdi-delete-sweep</v-icon>
            </v-btn>
          </div>

          <div class="selected-catalog-list">
            <div v-for="product in selectedProducts" :key="product._id" class="selected-catalog-item">
              <div class="d-flex align-center mb-2">
                <div class="catalog-image-thumb-small mr-2">
                  <img :src="parseProductImage(product)" :alt="product.title" />
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex align-center gap-2">
                    <div class="catalog-name-small">
                      {{ product.title }}
                    </div>
                  </div>
                </div>
                <v-btn icon x-small @click="removeSelectedProduct(product)">
                  <v-icon small color="error">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <div v-if="selectedProducts.length === 0" class="empty-selected">
              <v-icon large color="grey lighten-1">mdi-package-variant-closed</v-icon>
              <p class="grey--text text-center mt-2">No products selected</p>
              <p class="text-caption grey--text text-center">Select products from the left</p>
            </div>
          </div>
        </div>
      </div>

      <v-alert v-if="selectedProducts.length === 0 && showValidation" type="warning" dense text class="mt-3">
        Please select at least one product
      </v-alert>
    </v-form>

    <v-divider class="my-4"></v-divider>

    <div class="d-flex justify-end">
      <v-btn @click="$emit('close')" :height="40" class="mr-2" text>
        Cancel
      </v-btn>
      <v-btn depressed color="primary" :loading="form.loading" :height="40" @click="onSave">
        <v-icon left>mdi-content-save</v-icon>
        Save
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import { feedService } from '@/apis/feed';
import { catalogService } from '@/apis/catalog';
import { productApi } from '@/apis/product';
import imageHelpers from '@/helpers/image';

export default {
  name: 'FacebookCatalogDialog',
  props: {
    catalog: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: {
        fields: {
          catalog_name: '',
          fb_bm_id: null,
        },
        loading: false,
      },
      rules: {
        required: [v => (v && String(v).length > 0) || 'This field is required'],
      },
      products: [],
      selectedProducts: [],
      isLoading: false,
      loadingMore: false,
      searchText: '',
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
      },
      showValidation: false,
      debounceTimeout: null,
      bmList: [],
      isLoadingBms: false,
    };
  },
  computed: {
    hasMoreProducts() {
      return this.products.length < this.pagination.total;
    },
    formattedBmList() {
      return this.bmList.map(bm => ({
        ...bm,
        displayName:
          bm.business_name && bm.business_id
            ? `${bm.business_name} (${bm.business_id})`
            : bm.business_name || bm.business_id || '',
      }));
    },
  },
  methods: {
    async getFbBms() {
      try {
        this.isLoadingBms = true;
        const { data, status } = await catalogService.getFbBms({ page: 1, limit: 100 });
        if (status === 200) {
          this.bmList = data.data || [];
        }
      } catch (error) {
        console.error('Failed to load BMs:', error);
        this.$store.dispatch('setMessage', {
          color: 'error',
          dialog: true,
          content: 'Failed to load Business Managers',
        });
      } finally {
        this.isLoadingBms = false;
      }
    },

    async getProducts(append = false) {
      try {
        if (append) {
          this.loadingMore = true;
        } else {
          this.isLoading = true;
          this.pagination.page = 1;
        }

        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          search: this.searchText,
          published: true,
        };
        const { data, status } = await productApi.get(params);
        if (status === 200) {
          const fetchedProducts = data.products || [];
          if (append) {
            this.products = [...this.products, ...fetchedProducts];
          } else {
            this.products = fetchedProducts;
          }

          // Get total count
          const countRes = await productApi.count({ search: this.searchText, published: true });
          this.pagination.total = countRes.data.count || 0;
        }
      } catch (error) {
        this.$store.dispatch('setMessage', {
          color: 'error',
          dialog: true,
          content: 'Failed to load products',
        });
      } finally {
        this.isLoading = false;
        this.loadingMore = false;
      }
    },

    loadMoreProducts() {
      this.pagination.page += 1;
      this.getProducts(true);
    },

    debouncedSearch() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.getProducts(false);
      }, 500);
    },

    async selectAllAvailable() {
      try {
        this.isLoading = true;
        if (this.products.length < this.pagination.total) {
          const params = {
            page: 1,
            limit: this.pagination.total,
            search: this.searchText,
            published: true,
          };
          const { data, status } = await productApi.get(params);
          if (status === 200 && data.products) {
            this.products = data.products;
          }
        }

        const existingIds = new Set(this.selectedProducts.map(p => p._id));
        this.products.forEach(prod => {
          if (!existingIds.has(prod._id)) {
            this.selectedProducts.push(prod);
          }
        });
      } catch (error) {
        console.error('Failed to select all products:', error);
        this.$store.dispatch('setMessage', {
          color: 'error',
          dialog: true,
          content: 'Failed to select all products',
        });
      } finally {
        this.isLoading = false;
      }
    },

    isSelected(productId) {
      return this.selectedProducts.some(p => p._id === productId);
    },

    toggleProduct(product) {
      const index = this.selectedProducts.findIndex(p => p._id === product._id);

      if (index > -1) {
        this.selectedProducts.splice(index, 1);
      } else {
        this.selectedProducts.push(product);
      }
    },

    removeSelectedProduct(product) {
      const index = this.selectedProducts.findIndex(p => p._id === product._id);
      if (index > -1) {
        this.selectedProducts.splice(index, 1);
      }
    },

    parseProductImage(product) {
      const image = product?.images?.[0];
      if (!image) return require('@/assets/images/no-image.png');
      const src = typeof image === 'string' ? image : image.src;
      return imageHelpers.url(src, { width: 320, height: 320 });
    },

    clearAllSelected() {
      this.selectedProducts = [];
    },

    validateForm() {
      if (!this.$refs.formRef.validate()) {
        return false;
      }

      if (this.selectedProducts.length === 0) {
        this.showValidation = true;
        this.$store.dispatch('setMessage', {
          type: 'error',
          content: 'Please select at least one product',
        });
        return false;
      }

      return true;
    },

    async onSave() {
      if (!this.validateForm()) {
        return;
      }

      try {
        this.form.loading = true;
        const dataPost = {
          name: this.form.fields.catalog_name.trim(),
          product_id: this.selectedProducts.map(p => p._id),
          fb_bm_id: this.form.fields.fb_bm_id || undefined,
        };

        if (this.catalog) {
          await feedService.updateCatalog(this.catalog._id, dataPost);
          this.$store.commit('setMessages', {
            type: 'success',
            messages: 'Catalog updated successfully',
          });
        } else {
          await feedService.createCatalog(dataPost);
          this.$store.commit('setMessages', {
            type: 'success',
            messages: 'Catalog created successfully',
          });
        }

        this.$emit('close', true);
      } catch (error) {
        console.error('❌ Error saving catalog:', error);
        const errorMessage = error?.response?.data?.message || 'Failed to save catalog';
        this.$store.commit('setMessages', {
          type: 'error',
          messages: errorMessage,
        });
      } finally {
        this.form.loading = false;
      }
    },
  },
  mounted() {
    this.getProducts();
    this.getFbBms();
    if (this.catalog) {
      this.form.fields.catalog_name = this.catalog.name || '';
      this.form.fields.fb_bm_id = this.catalog.fbBmId || null;
      this.selectedProducts = (this.catalog.products_detail || []).map(p => ({
        _id: p.id || p._id,
        title: p.title,
        images: p.imageSrcs || p.images || [],
      }));
    }
  },
  beforeDestroy() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  },
};
</script>

<style scoped>
.field {
  margin-bottom: 16px;
}

.field .title {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 14px;
}

/* Two Column Layout */
.select-products-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  min-height: 500px;
}

.left-column,
.right-column {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background-color: #fafafa;
}

.column-header {
  margin-bottom: 12px;
}

/* Catalog List - Left Column */
.catalog-list {
  max-height: 500px;
  overflow-y: auto;
  margin-top: 12px;
}

.catalog-image-thumb {
  width: 48px;
  height: 48px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.catalog-image-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.catalog-image-thumb-small {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.catalog-image-thumb-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.catalog-item {
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.catalog-item:hover {
  border-color: #1976d2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.catalog-item.selected {
  border-color: #1976d2;
  background-color: #e3f2fd;
}

.catalog-name {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

/* Selected Catalogs - Right Column */
.selected-catalog-list {
  max-height: 580px;
  overflow-y: auto;
}

.selected-catalog-item {
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.catalog-name-small {
  font-weight: 500;
  font-size: 13px;
  color: #333;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-clamp: 1;
}

.retailer-id-input {
  flex: 1;
  min-width: 0;
}

.gap-2 {
  gap: 12px;
}

.empty-selected {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

/* Scrollbar Styling */
.catalog-list::-webkit-scrollbar,
.selected-catalog-list::-webkit-scrollbar {
  width: 6px;
}

.catalog-list::-webkit-scrollbar-track,
.selected-catalog-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.catalog-list::-webkit-scrollbar-thumb,
.selected-catalog-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.catalog-list::-webkit-scrollbar-thumb:hover,
.selected-catalog-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Responsive */
@media (max-width: 960px) {
  .select-products-container {
    grid-template-columns: 1fr;
  }
}
</style>
